import { ApolloProvider } from "@apollo/client";
import { parseTokensFromUrl } from "@common/keycloakAuth";
import { muiTheme } from "@common/themes";
import { ThemeProvider } from "@mui/material/styles";
import { createBrowserApp } from "@react-navigation/web";
import queryClient from "@src/reactQuery";
import { QueryClientProvider } from "@tanstack/react-query";
import { clarity } from "clarity-js";
import React, { Component, Suspense } from "react";
import { View } from "react-native";
import "typeface-roboto";

import "@assets/fonts/index.css";
import { CustomSnackbar, ErrorMsg, ImageLoading } from "./common/component";
import { subscribePusher, unsubscribePusher } from "./common/pusher";
import { THEME_COLORS } from "./common/styles";
import Routes from "./navigation";
import { SparesProvider } from "./screen/SparePartsScreen/context/SparesContext";
import { BudgetProvider } from "./screen/v3/BudgetModule/context/BudgetContext";
import { ValidationProvider } from "./screen/v3/InspectionAuditModule/context/InspectionContext";
import { ConfirmationPendingProvider } from "./src/context/ConfirmationPendingContext";
import { InventoryReplenishmentProvider } from "./src/context/InventoryReplenishmentContext";
import { InvoiceProvider } from "./src/context/InvoiceContext";
import { InvoicingPendingProvider } from "./src/context/InvoicingPendingContext";
import { OpenMarketProvider } from "./src/context/OpenMarketContext";
import { PurchaserProvider } from "./src/context/PurchaserContext";
import { ReceivingPendingProvider } from "./src/context/ReceivingPendingContext";
import { UserProvider } from "./src/context/UserContext";

import {
  client,
  getCookie,
  LOCAL_VERSION_API,
  SPINNY_REST_JWT_TOKEN_REFRESH_URL,
  SPINNY_WEB_TOKEN_REST,
  SPINNY_WEB_TOKEN_REST_REFRESH,
  VERSION,
} from "./common";

// Added a comment in App.js
const AppRoute = createBrowserApp(Routes);

if (process.env.NODE_ENV !== "development") {
  if (!window.console) window.console = {};
  const methods = ["log", "debug", "warn", "info"];
  methods.forEach((method) => {
    console[method] = function () {};
  });
}

export const RootContext = React.createContext();

class App extends Component {
  state = {
    isUserLoggedIn: false,
    versionErr: false,
    value: {},
    setValue: this.setValue,
    snackbar: { open: false },
    hasSubHeader: {
      part_receiving: false,
      pr_job_fulfilment: true,
    },
    count: {
      spare: 0,
      refurb: 0,
    },
    spareScreens: [],
  };

  constructor(props) {
    super(props);
    this.setValue = this.setValue.bind(this);
    // setCustomText(customTextProps);
    
    parseTokensFromUrl();
    setInterval(() => {
      const token = localStorage.getItem(SPINNY_WEB_TOKEN_REST);
      const refresh_token = localStorage.getItem(SPINNY_WEB_TOKEN_REST_REFRESH);
      if (!!token && !!refresh_token) {
        fetch(SPINNY_REST_JWT_TOKEN_REFRESH_URL, {
          method: "POST",
          body: JSON.stringify({ refresh: refresh_token }),
        })
          .then((res) => res.json())
          .then((data) => {
            localStorage.setItem(SPINNY_WEB_TOKEN_REST, `Bearer ${data.access}`);
          })
          .catch((e) => console.log(e));
      }
    }, 180000);

    setInterval(() => {
      fetch(LOCAL_VERSION_API)
        .then((res) => res.json())
        .then((data) => {
          if (data?.version && VERSION && data.version !== VERSION) {
            this.setState({
              versionErr: true,
            });
          } else {
            this.setState({
              versionErr: false,
            });
          }
        })
        .catch((e) => console.log(e.message));
    }, 15 * 60000);
    this.notificationsRef = React.createRef([]);
    this.entityRef = React.createRef(null);
    this.notificationCountRef = React.createRef({});
    this.notificationCountRef.current = this.state.count;
  }

  componentDidMount() {
    document.addEventListener(
      "onShowAlert",
      (e) => {
        this.setState({ snackbar: { open: true, message: e.detail.message, severity: e.detail.severity } });
      },
      false
    );
    subscribePusher();
    if (process.env.NODE_ENV === "production") {
      clarity.consent();
      clarity.set("workshop", getCookie("workshop"));
      clarity.start({
        projectId: "fjskuj6qge",
        upload: "https://m.clarity.ms/collect",
        track: true,
        content: true,
        cookies: ["workshop"],
      });
    } else {
      clarity.consent();
      clarity.set("workshop", getCookie("workshop"));
      clarity.start({
        projectId: "fh6zgluphk",
        upload: "https://m.clarity.ms/collect",
        track: true,
        content: true,
        cookies: ["workshop"],
      });
    }
  }

  componentWillUnmount() {
    clarity.stop();
    unsubscribePusher();
  }

  setValue(updatedValue) {
    this.setState((prevstate) => ({ value: { ...prevstate.value, ...updatedValue } }));
  }

  closeSnackbar = () => {
    this.setState({ snackbar: { open: false } });
  };

  updateHasSubHeader = (updatedSubHeader) => {
    this.setState({ hasSubHeader: updatedSubHeader });
  };

  closeSnackbar = () => {
    this.setState({ snackbar: { open: false } });
  };

  updateNotificationCount = (type, newCount) => {
    let prevCount = this.state.count;
    if (type === "spare") {
      prevCount.spare = newCount;
    } else if (type === "refurb") {
      prevCount.refurb = newCount;
    }
    this.notificationCountRef.current = prevCount;
    this.setState({ count: prevCount });
  };

  updateSpareScreens = (spareScreens) => {
    this.setState({ spareScreens: spareScreens });
  };

  render() {
    const { versionErr, snackbar, hasSubHeader } = this.state;

    return (
      <>
        {versionErr && (
          <View style={{ position: "absolute", width: "100%", alignItems: "center" }}>
            <ErrorMsg errorMsg="A new version has been released, press Ctrl+Shift+R to reload" showBox={true} />
          </View>
        )}
        <View
          style={{
            backgroundColor: THEME_COLORS.white,
            position: "absolute",
            top: versionErr ? 50 : 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
          }}
        >
          <ApolloProvider client={client}>
            <QueryClientProvider client={queryClient}>
              <ThemeProvider theme={muiTheme}>
                <RootContext.Provider
                  value={{
                    value: this.state.value,
                    hasSubHeader,
                    updateHasSubHeader: this.updateHasSubHeader,
                    setValue: this.setValue,
                  }}
                >
                  <UserProvider>
                    <SparesProvider>
                      <PurchaserProvider>
                        <ConfirmationPendingProvider>
                          <ReceivingPendingProvider>
                            <OpenMarketProvider>
                              <InventoryReplenishmentProvider>
                                <InvoiceProvider>
                                  <InvoicingPendingProvider>
                                    <BudgetProvider>
                                      <ValidationProvider>
                                        <Suspense fallback={<ImageLoading />}>
                                          <AppRoute
                                            screenProps={{
                                              entityRef: this.entityRef,
                                              sparesTabs: this.state.spareScreens,
                                              notificationsRef: this.notificationsRef,
                                              notificationCountRef: this.notificationCountRef,
                                              updateNotificationCount: this.updateNotificationCount,
                                              updateSpareScreens: this.updateSpareScreens,
                                            }}
                                          />
                                        </Suspense>
                                      </ValidationProvider>
                                    </BudgetProvider>
                                  </InvoicingPendingProvider>
                                </InvoiceProvider>
                              </InventoryReplenishmentProvider>
                            </OpenMarketProvider>
                          </ReceivingPendingProvider>
                        </ConfirmationPendingProvider>
                      </PurchaserProvider>
                    </SparesProvider>
                  </UserProvider>
                </RootContext.Provider>
              </ThemeProvider>
            </QueryClientProvider>
          </ApolloProvider>
        </View>
        <CustomSnackbar
          open={snackbar.open}
          message={snackbar.message}
          handleClose={() => this.closeSnackbar()}
          severity={snackbar.severity || "info"}
        />
      </>
    );
  }
}

export default App;